<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, Input, Text, Textarea, Token } from '@Shelly/shelly-design-system';
	import { clearQuotes } from 'src/helpers/actions/webhooks/conditions/webhook_conditions_helpers';
	import { usesSupportedWebhookAttribute } from 'src/helpers/actions/webhooks/view/create_webhook_save_helpers';
	//Types
	import type { WebHookEventOption } from 'src/types/requests/settings/webhooks';

	export let eventsOptions: WebHookEventOption[] = [];
	export let error: { show: boolean; label: string };
	export let eventsIndex = -1;

	const selectAttribute = (index: number) => {
		conditionOptions.forEach((condition) => (condition.conditionsIndex = -1));
		if (conditionOptions[index].isRawExpression) conditionOptions[index].conditionsIndex = 0;
		eventsOptions = [...eventsOptions];
	};

	const selectOperator = (index: number) => {
		selectedAttribute.conditionsIndex = index;
		eventsOptions = [...eventsOptions];
	};

	const setConditionValue = (value: string) => {
		selectedAttribute.conditionValue = value;
		eventsOptions = [...eventsOptions];
	};

	$: conditionOptions = eventsOptions[eventsIndex]?.conditions || [];
	$: selectedAttribute =
		conditionOptions.find((condition) => condition.conditionsIndex >= 0) || conditionOptions[0];
	$: rawExpression = String(selectedAttribute?.conditionValue || '').trim();
	$: unsupportedRawExpression =
		selectedAttribute?.isRawExpression &&
		rawExpression.length > 0 &&
		!usesSupportedWebhookAttribute(rawExpression, selectedAttribute.tokens);
</script>

{#if conditionOptions.length}
	<Container type="borderDashed" class="mt-m" padding="m">
		<slot name="lnm-component-selector" />
		<Text element="h2" class="mb-s mt-m">
			{$format('label_attribute', { default: 'Attribute' })}
		</Text>
		<div class="d-flex flex-wrap" style="gap: var(--spacing-s);">
			{#each conditionOptions as condition, index}
				<Token
					id={`token-lnm-attribute-${condition.attrName}`}
					size="m"
					title={condition.label}
					selected={selectedAttribute === condition}
					on:click={() => selectAttribute(index)}
				/>
			{/each}
		</div>

		{#if selectedAttribute?.isRawExpression}
			<Textarea
				id="textarea-lnm-condition-expression"
				aria-labelledby="textarea-lnm-condition-expression"
				size="m"
				resize="auto"
				class="mt-m width-full"
				label={selectedAttribute.label}
				value={String(selectedAttribute.conditionValue)}
				error={error.show && !String(selectedAttribute.conditionValue || '').trim()
					? error.label
					: ''}
				on:input={(event) => setConditionValue(event.detail.target.value)}
			/>
			{#if unsupportedRawExpression}
				<Text styleType="body-small" class="mt-s text-warning">
					<em>
						{$format('lnm_unsupported_condition_warning', {
							default:
								'This condition syntax deviates from the supported attributes and may not work properly.',
						})}
					</em>
				</Text>
			{/if}
			<Text styleType="body-small" class="mt-s text-color-muted">
				<em>
					{#if selectedAttribute.attrName === 'status'}
						{$format('lnm_received_status_description', {
							default:
								"Webhook event will receive status object from the device in LNM network. Write an expression like ev.status['switch:0'].output === true.",
						})}
					{:else}
						{$format('lnm_received_event_description', {
							default:
								'Webhook event will receive event object from the device in LNM network. Write an expression using ev.event_data.',
						})}
					{/if}
				</em>
			</Text>
		{:else if selectedAttribute}
			<Text element="h2" class="mt-m mb-s">
				{$format('label_condition', { default: 'Condition' })}
			</Text>
			<div class="d-flex flex-wrap" style="gap: var(--spacing-s);">
				{#each selectedAttribute.conditions as condition}
					<Token
						id={`token-lnm-condition-${condition.index}`}
						size="m"
						title={condition.title}
						selected={selectedAttribute.conditionsIndex === condition.index}
						on:click={() => selectOperator(condition.index)}
					/>
				{/each}
			</div>
			{#if selectedAttribute.conditionsIndex >= 0}
				<Input
					id="input-lnm-condition-value"
					aria-labelledby="input-lnm-condition-value"
					size="m"
					class="mt-m width-full"
					label={selectedAttribute.label}
					value={clearQuotes(String(selectedAttribute.conditionValue))}
					error={error.show && !clearQuotes(String(selectedAttribute.conditionValue)).trim()
						? error.label
						: ''}
					icon="fa fa-pen"
					on:input={(e) => {
						setConditionValue(JSON.stringify(e.target['value']));
					}}
				/>
			{/if}
		{/if}
	</Container>
{/if}
